
import * as React from 'react';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import { Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { TruckModel, AddNewTruckModel } from './TruckModels';
import { localizations } from '../localization/localization';
import Add from '@material-ui/icons/Add';
import { momentToString, dateStringSort } from '../utils/dateUtilities';
import * as TruckStore from './store/truckStore';
import { connect } from 'react-redux';
import { ApplicationState } from '../store';
import AddFreeTruckForm from './forms/AddFreeTruckForm';
import { RowInfo, Column } from "react-table";
import ViewHeaderBar from '../components/ViewHeaderBar';
import FreeTruckRowDetails from './FreeTruckRowDetails';
import { withExpander, withHeaderStyle } from '../components/tables/TableUtils';
import customTheme from '../common/theme';
import DeleteTruckDialog from './forms/DeleteTruckDialog';
import TruckEmailDialog from './forms/TruckEmailDialog';
import * as LoginStore from '../login/loginStore';
import * as FsStore from '../account/store/FilterSettingsStore';
import { FilterInfo } from '../common/models/CommonModels';
import Own from '@material-ui/icons/Person';
import Company from '@material-ui/icons/People';
import * as moment from 'moment';
import Deleted from '@material-ui/icons/Delete';
import { push } from 'react-router-redux';
import { bindActionCreators } from 'redux';
import { RouteName } from '../routeNames';
import FilteredTableWithPaging from '../components/tables/FilteredTableWithPaging';
import { PagingFilter } from '../common/models/PagingModels';
import { toCommaDecimal } from '../utils/numberUtils';
import { RecurrenceType } from '../common/models/Recurrence';
import { common } from '@material-ui/core/colors';
import { UserRole } from '../login/LoginModels';

const styles = (theme: Theme) => ({
    leftIcon: {
        marginRight: theme.spacing(1),
    },
});

// Column specification for truck list.
const columns: Column[] = withHeaderStyle(customTheme)((withExpander([
    {
        Header: "",
        id: "isOwn",
        accessor: (t: TruckModel) => {
            let value = "";

            if (t.isOwn) {
                value = "1"
            }
            else if (t.isOwnCompany) {
                value = "2"
            }
            else {
                value = "3"
            }

            if (t.deletedAt) {
                value += "d";
            }

            return value;
        },
        Cell: row => {
            const t = row.original as TruckModel;

            return (
                <span>
                    {t.isOwn ? <Tooltip title={localizations.company.personalAd}><span><Own color={t.deletedAt ? "disabled" : "secondary"} /></span></Tooltip> : t.isOwnCompany ? <Tooltip title={localizations.common.companyAd}><span><Company color={t.deletedAt ? "disabled" : "secondary"} /></span></Tooltip> : null}
                    {t.deletedAt ? <Tooltip title={localizations.common.deletedAt + " " + momentToString(t.deletedAt)}><span><Deleted color="disabled" /></span></Tooltip> : null}
                </span>
            );
        },
        width: 60,
        sortable: true,
    },
    {
        Header: localizations.common.reportNumberCol,
        accessor: "reportNumber",
        maxWidth: 100
    },
    {
        Header: localizations.common.startTime,
        id: "availableFrom",
        accessor: (t: TruckModel) => momentToString(t.availableFrom),
        sortMethod: dateStringSort,
        Cell: row => {
            const truck = row.original as TruckModel;
            if (truck) {
                if (truck.deletedAt) {
                    return <span style={{ color: "gray" }}>{row.value}</span>
                }

                const now = moment();

                if (truck.availableFrom < now) {
                    return <span style={{ color: "red" }}>{row.value}</span>
                }
            }
            return <span>{row.value}</span>
        },
    },
    {
        Header: localizations.common.endTime,
        id: "availableTo",
        accessor: (t: TruckModel) => momentToString(t.availableTo),
        sortMethod: dateStringSort,
        Cell: row => {
            const truck = row.original as TruckModel;
            const now = moment();

            if (truck) {
                if (truck.deletedAt) {
                    return <span style={{ color: "gray" }}>{row.value}</span>
                }

                if (truck.availableTo < now) {
                    return <span style={{ color: "red" }}>{row.value}</span>
                }
            }
            
            const currDate = now.format('DD-MM-YYYY');
            const deliDate = truck.availableTo.format('DD-MM-YYYY');
            if (currDate === deliDate) {
                const timeDiff = truck.availableTo.diff(now);
                if (timeDiff > 0) {
                    // duration in hours
                    const hours = truck.availableTo.diff(now, 'hours');

                    // duration in minutes
                    const minutes = truck.availableTo.diff(now, 'minutes')%60;
                    const minutesStr = ("0"+minutes).slice(-2);

                    if (hours < 1) {
                        return <span style={{ color: "red" }}>{row.value}, {localizations.common.timeLeft} {minutes}{localizations.common.min}</span>
                    }
                    return <span style={{ color: "red" }}>{row.value}, {localizations.common.timeLeft} {hours}{localizations.common.h} {minutesStr}{localizations.common.min}</span>
                }

                return <span style={{ color: "red" }}>{row.value}</span>
            }
            return <span>{row.value}</span>
        },
    },
    {
        Header: localizations.common.pointOfDeparture,
        id: "pointOfDeparture",
        accessor: (d: TruckModel) => d.departureCity.name,
        Cell: row => {
            const truck = row.original as TruckModel;
            if (truck) {
                if (truck.deletedAt) {
                    return <span style={{ color: "gray" }}>{row.value}</span>
                }
            }
            return <span>{row.value}</span>
        },
    },
    {
        Header: localizations.common.pointOfArrival,
        id: "pointOfArrival",
        accessor: (d: TruckModel) => d.destinationCity ? d.destinationCity.name : "",
        Cell: row => {
            const truck = row.original as TruckModel;
            if (truck) {
                if (truck.deletedAt) {
                    return <span style={{ color: "gray" }}>{row.value}</span>
                }
            }
            return <span>{row.value}</span>
        },
    },
    {
        Header: localizations.common.contractPriceCol,
        id: "contractPrice",
        accessor: (t: TruckModel) => t.contractPrice ? `${toCommaDecimal(t.contractPrice)} €/km` : undefined
    }
])));

interface TrucksViewState {
    refreshFilters: boolean;
}

const TrucksTable = FilteredTableWithPaging as new () => FilteredTableWithPaging<TruckModel>

type WithStylesProps = "leftIcon";

type FreeTrucksViewProps = WithStyles<WithStylesProps> 
    & { filterSettingsActions: typeof FsStore.actionsCreators, 
        truckActions: typeof TruckStore.actionCreators, 
        goToActions: typeof goActions } 
    & { filterSettings: FsStore.FilterSettingsState, trucks: TruckStore.TrucksState, login: LoginStore.LoginState };

class FreeTrucksView extends React.Component<FreeTrucksViewProps, TrucksViewState> {
    constructor(props: FreeTrucksViewProps) {
        super(props);

        this.state = {
            refreshFilters: false
        }

        this.toSubComponent = this.toSubComponent.bind(this);
        this.handleEdit = this.handleEdit.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.handleFetch = this.handleFetch.bind(this);
        this.handleCopy = this.handleCopy.bind(this);
        this.isOwnCompany = this.isOwnCompany.bind(this);
        this.isEditEnabled = this.isEditEnabled.bind(this);
        this.isDeleteEnabled = this.isDeleteEnabled.bind(this);
        this.isCopyEnabled = this.isCopyEnabled.bind(this);
        this.handleGoToMap = this.handleGoToMap.bind(this);
        this.handleAddTruck = this.handleAddTruck.bind(this);
        this.afterEdit = this.afterEdit.bind(this);
        this.handleSendEmail = this.handleSendEmail.bind(this);
        this.handleSendEmailToReporter = this.handleSendEmailToReporter.bind(this);
    }

    public render() {
        const { classes } = this.props;
        const { isLoading, data } = this.props.trucks;
        const adding = this.props.trucks.isEditing;
        
        // Check if item is selected in store.
        const isEditMode = adding && !!this.props.trucks.editedItem;

        // Check if item is wanted to be deleted.
        const deleting = this.props.trucks.isDeleting;

        const initialSearchFilter = (this.props.trucks.selected && this.props.trucks.selected.reportNumber) ? this.props.trucks.selected.reportNumber : "";

        const isDialogOpen = this.props.trucks.isSendingEmail || this.props.trucks.isSendingEmailToReporter;

        return (
            <div>
                <ViewHeaderBar title={localizations.menus.freeTrucks} showHelp helpAnchor="trucksview">
                    <Button variant="contained" color="secondary" onClick={this.handleAddTruck}>
                        <Add className={classes.leftIcon} />{localizations.actions.addNew}
                    </Button>
                </ViewHeaderBar>
                <TrucksTable
                    data={data}
                    isLoading={isLoading}
                    columns={columns}
                    onFetchData={this.handleFetch}
                    filterIncludes={{ trucks: true, units: false }}
                    selected={this.props.trucks.selected}
                    toSubComponent={this.toSubComponent}
                    initialFilterValues={{ reportNumberSearch: initialSearchFilter }}
                    defaultSortedColumns={[
                        { id: "availableFrom", desc: true }
                    ]}
                    refreshSwitch={this.state.refreshFilters}
                    onSaveFilter={this.props.filterSettingsActions.AddOrEditFilterSetting}
                    onSaveFilterCancel={this.props.filterSettingsActions.addOrEditFormCancelled}
                    onSaveFilterSubmit={this.props.filterSettingsActions.addOrEditFormSubmitted}
                    updateSaveFilterDestinationCities={this.props.filterSettingsActions.updateNewFilterSettingsDestinationCities}
                    updateSaveFilterDepartureCities={this.props.filterSettingsActions.updateNewFilterSettingsDepartureCities}
                    updateSaveFilterReporters={this.props.filterSettingsActions.updateNewFilterSettingsReporters}
                    filterSettings={this.props.filterSettings}
                />
                {adding ? (
                    <AddFreeTruckForm
                        isOpen={adding}
                        isEditMode={isEditMode}
                        editMode={this.props.trucks.editMode}
                        onSubmit={this.props.truckActions.addOrEditFormSubmitted}
                        onCancel={this.props.truckActions.addOrEditFormCancelled}
                    />
                 ) : null}
                {this.props.trucks.toDeleteItem ? (
                    <DeleteTruckDialog
                        isOpen={deleting}
                        onSubmit={this.props.truckActions.deleteSubmitted}
                        onCancel={this.props.truckActions.deleteCancelled}
                        truck={this.props.trucks.toDeleteItem}
                        companyId={this.props.trucks.toDeleteItem.companyId}
                    />
                 ) : null}
                {isDialogOpen ? (
                    <TruckEmailDialog
                        isOpen={isDialogOpen}
                        onSubmit={this.props.trucks.isSendingEmail ? this.props.truckActions.sendEmailSubmitted : this.props.truckActions.sendEmailToReporterSubmitted}
                        onCancel={this.props.trucks.isSendingEmail ? this.props.truckActions.sendEmailCancelled : this.props.truckActions.sendEmailToReporterCancelled}
                        toReporter={this.props.trucks.isSendingEmailToReporter}
                    />
                ) : null}
            </div>
        );
    }

    private refreshFilters() {
        this.setState({
            refreshFilters: !this.state.refreshFilters
        });
    }

    /*
        Sub component specification for truck list row.
    */
    private toSubComponent(row: RowInfo) {
        return <div id={row.original.id}>
            <FreeTruckRowDetails
                {...row.original}
                onEdit={this.handleEdit}
                isEditEnabled={this.isEditEnabled}
                isDeleteEnabled={this.isDeleteEnabled}
                onDelete={this.handleDelete}
                isCopyEnabled={this.isCopyEnabled}
                onCopy={this.handleCopy}
                onGoToMap={this.handleGoToMap}
                onSendEmail={this.handleSendEmail}
                onSendEmailToReporter={this.handleSendEmailToReporter}
            />
        </div>
    }

    private handleAddTruck() {
        this.props.truckActions.addOrEditTruck(undefined, this.afterEdit, true);
    }

    private handleGoToMap(truck: TruckModel) {
        this.props.truckActions.selectTruck(truck);

        this.props.goToActions.goTo(RouteName.Map);
    }

    private handleDelete(truck: TruckModel) {
        this.props.truckActions.deleteTruck(truck, this.afterEdit);
    }

    private handleFetch(filters: FilterInfo, paging: PagingFilter) {
        // Cache filters just in case to be used after add or delete.
        this.props.truckActions.cacheFilters(filters);
        this.props.truckActions.requestFreeTrucks(filters, true);

        this.props.truckActions.selectTruck(null);
        this.props.truckActions.requestTruckRoutes();
    }

    private handleEdit(truck: TruckModel) {
        const truckData = {
            ...truck,
            destinationCity: truck.destinationCity ? truck.destinationCity : { id: "", name: "" },
            details: truck.details ? truck.details : "",
            plateNumber: truck.plateNumber ? truck.plateNumber : "",
            noDestination: !truck.destinationCity,
            recurrence: {
                ...truck.recurrence,
                weekdays: truck.recurrence.weekdays ? truck.recurrence.weekdays : {
                    monday: false,
                    tuesday: false,
                    wednesday: false,
                    thursday: false,
                    friday: false,
                    saturday: false,
                    sunday: false
                },
                everyNthOccurrence: truck.recurrence.everyNthOccurrence ? truck.recurrence.everyNthOccurrence : 1,
                maxOccurrences: truck.recurrence.maxOccurrences ? truck.recurrence.maxOccurrences : 1,
                type: "weekly" as RecurrenceType,
                startDate: truck.recurrence.startDate ? truck.recurrence.startDate : truck.availableFrom,
                endDate: truck.recurrence.endDate ? truck.recurrence.endDate : truck.availableFrom.clone().add(1, 'days')
            }
        };
        this.props.truckActions.addOrEditTruck(truckData, this.afterEdit, true);
    }

    private afterEdit() {
        this.refreshFilters();
    }

    private handleCopy(truck: TruckModel) {
        const truckData = {
            ...truck,
            destinationCity: truck.destinationCity ? truck.destinationCity : { id: "", name: "" },
            details: truck.details ? truck.details : "",
            plateNumber: truck.plateNumber ? truck.plateNumber : "",
            noDestination: !truck.destinationCity,
            recurrence: {
                ...truck.recurrence,
                weekdays: truck.recurrence.weekdays ? truck.recurrence.weekdays : {
                    monday: false,
                    tuesday: false,
                    wednesday: false,
                    thursday: false,
                    friday: false,
                    saturday: false,
                    sunday: false
                },
                everyNthOccurrence: truck.recurrence.everyNthOccurrence ? truck.recurrence.everyNthOccurrence : 1,
                maxOccurrences: truck.recurrence.maxOccurrences ? truck.recurrence.maxOccurrences : 1,
                type: "weekly" as RecurrenceType,
                startDate: truck.recurrence.startDate ? truck.recurrence.startDate : truck.availableFrom,
                endDate: truck.recurrence.endDate ? truck.recurrence.endDate : truck.availableFrom.clone().add(1, 'days')
            }
        };
        this.props.truckActions.copyTruck(truckData);
    }

    private isOwnCompany(truck: TruckModel) {
        return truck.company.id === this.props.login.companyId;
    }

    private isEditEnabled(truck: TruckModel) {
        return (this.isOwnCompany(truck) || this.props.login.role === UserRole.Admin) && !truck.deletedAt;
    }

    private isDeleteEnabled(truck: TruckModel) {
        return (this.isOwnCompany(truck) || this.props.login.role === UserRole.Admin) && !truck.deletedAt;
    }

    private isCopyEnabled(truck: TruckModel) {
        return (this.isOwnCompany(truck) || this.props.login.role === UserRole.Admin) && !truck.deletedAt;
    }
    
    private handleSendEmail(truck: TruckModel) {
        this.props.truckActions.sendEmail(truck);
    }
    
    private handleSendEmailToReporter(truck: TruckModel) {
        this.props.truckActions.sendEmailToReporter(truck);
    }
}

const mapStateToProps = (state: ApplicationState) => {
    const { trucks, login, filterSettings } = state;
    return { trucks, login, filterSettings };
}

const goActions = {
    goTo: (location: string) => push(location)
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        filterSettingsActions: bindActionCreators(FsStore.actionsCreators, dispatch),
        truckActions: bindActionCreators(TruckStore.actionCreators, dispatch),
        goToActions: bindActionCreators(goActions, dispatch)
    }
}

export default withStyles(styles)(connect<any, any, any>(mapStateToProps, mapDispatchToProps)(FreeTrucksView));



// WEBPACK FOOTER //
// ./src/trucks/FreeTrucksView.tsx